import ObjectType, { ObjectAnimationType } from '../../../constants/ObjectType';
import { GeometriesFactory } from './GeometriesFactory';
import { TexturesStorage } from './TexturesStorage';
import { MaterialsFactory } from './MaterialsFactory';
import settings from '../../../../data/settings.json';
import ObjectsHolder from '../objects/ObjectsHolder';
import AnimatedButterflyWingsObject3D from '../objects/animated/AnimatedButterflyWingsObject3D';
import AnimatedDragonflyWingsObject3D from '../objects/animated/AnimatedDragonflyWingsObject3D';
import WindOscillateObject3D from '../objects/animated/WindOscillateObject3D';

const THREE = require('three');

const defaults = {
    objectsLib: { ...settings.objectsLib },
    materialsLib: { ...settings.materialsLib }
};

const selectConfigFromLibIfThereIs = (data, lib) => {
    if (typeof data.key === 'string') {
        const libData = lib[data.key] ?? {};
        // TODO: FIX THE MISTAKE ?
        const result = { ...libData, ...data };
        // const result = { ...data, ...libData };

        delete result.key;

        return result;
    }

    return { ...data };
};

const setChildrenMaterial = (obj3D, matrl, matrlAdditionalMap, recursive = true) => {
    const { children } = obj3D;
    const checkMaterial = !!matrlAdditionalMap;

    children.forEach((ch) => {
        if (recursive) setChildrenMaterial(ch, matrl, matrlAdditionalMap);

        if (!checkMaterial) {
            ch.material = matrl;

            return;
        }
        ch.material = matrlAdditionalMap[ch.name] ?? matrl;
    });
};

const constructThreeObject = async (data) => {
    const {
        geometry,
        material,
        materialOverride,
        materialMap,
        materialMapOverride,
        name,
        reflect
    } = data;

    if (!material || !geometry) return null;

    const geom = await GeometriesFactory.create(geometry.type, geometry);

    if (!geom) return null;

    const materialConfig = selectConfigFromLibIfThereIs(materialOverride || material, defaults.materialsLib);
    let materialMapConfig = materialMap || materialMapOverride;

    if (materialMapConfig) {
        materialMapConfig = materialMapConfig
            .map((mc) => selectConfigFromLibIfThereIs(mc, defaults.materialsLib));
    }

    const { textures, texturesTargetParam } = materialConfig;

    if (textures instanceof Array) {
        delete materialConfig.textures;
        delete materialConfig.texturesTargetParam;

        materialConfig[texturesTargetParam ?? 'map'] = await TexturesStorage.getTexture({ options: textures }, name);
    }

    const matrl = MaterialsFactory.create(materialConfig, material.type);
    let matrlAdditionalMap = null;

    if (materialMapConfig) {
        matrlAdditionalMap = {};

        await Promise.all(materialMapConfig.map(async (mc) => {
            if (textures instanceof Array) {
                delete mc.textures;
                delete mc.texturesTargetParam;

                mc[texturesTargetParam ?? 'map'] = await TexturesStorage.getTexture({ options: textures }, name);
            }

            const { objectName } = mc;

            delete mc.objectName;
            matrlAdditionalMap[objectName] = MaterialsFactory.create(mc, mc.type);
        }));
    }

    // Only meshes for this project.
    // Add switch instruction to choose constructor if it's necessary

    let obj3D;
    let reflection3D;

    if (geom instanceof THREE.Geometry || geom instanceof THREE.BufferGeometry) {
        obj3D = new THREE.Mesh(geom, matrl);
        reflection3D = new THREE.Mesh(geom, matrl);
    } else if (geom instanceof THREE.Object3D) {
        setChildrenMaterial(geom, matrl, matrlAdditionalMap);

        obj3D = geom;
    }

    return {
        reflect,
        obj3D,
        reflection3D: reflect ? (reflection3D || obj3D.clone()) : null,
        geometry: geom,
        material: matrl
    };
};

const AnimationEngineFactory = {
    create(data, webgl, type, obj3D, reflection3D) {
        let result;

        switch (type) {
            case ObjectAnimationType.BUTTERFLY_WINGS:
                result = new AnimatedButterflyWingsObject3D(data, webgl, obj3D, reflection3D);

                break;

            case ObjectAnimationType.DRAGONFLY_WINGS:
                result = new AnimatedDragonflyWingsObject3D(data, webgl, obj3D, reflection3D);

                break;

            case ObjectAnimationType.WIND_OSCILLATE:
                result = new WindOscillateObject3D(data, webgl, obj3D, reflection3D);

                break;

            default:
                result = obj3D;

                break;
        }

        return result;
    }
};

export const ObjectFactory = {
    async create(data, webgl, innerFactory) {
        let obj3D = null;
        let reflection3D = null;
        let result = null;

        const dataCompiled = selectConfigFromLibIfThereIs(data, defaults.objectsLib);

        const { type, animationType } = dataCompiled;

        switch (type) {
            case ObjectType.MESH:
                result = await constructThreeObject(dataCompiled, false);
                obj3D = result?.obj3D;
                reflection3D = result?.reflection3D;

                break;

            case ObjectType.COMPOSITION3D:
                // Compositions don't have reflections
                obj3D = new ObjectsHolder(dataCompiled, webgl, null, innerFactory, true);
                result = { obj3D };

                break;

            case ObjectType.ANIMATION3D:
                result = await constructThreeObject(dataCompiled, false);
                obj3D = result?.obj3D;
                reflection3D = result?.reflection3D;

                obj3D = AnimationEngineFactory.create(dataCompiled, webgl, animationType, obj3D, reflection3D);
                result.obj3D = obj3D;
                result.reflection3D = obj3D.reflectionShell;

                break;

            default:
                obj3D = new THREE.Group();

                break;
        }

        if (obj3D) {
            obj3D.name = data.name;
            if (reflection3D) reflection3D.name = `${data.name}Reflection`;
        } else {
            console.warn(`Fail to construct Object '${data.name}' of type '${ObjectType.MESH}'`, result, data);
        }

        // Objects with reflections have to have there own position properties.
        // Otherwise they will appear at the same place.

        return { ...result, reflect: !!reflection3D };
    }
};
